import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import { ArticleNameCell } from '@/components/ArticlesTableCells';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconRefusal from '@/icons/Refusal.svg';
import IconUndo from '@/icons/Undo.svg';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { ArticleCancellationDetail, CLIENT_ARTICLE_CANCELLATION_REASONS } from '@/models/article';
import { ArticleWithRelations, useIsCompletingValidation } from '@/models/request';
import useViewPort from '@/utils/useViewport';

export type Choice = {
  value: 'refused' | 'accepted';
  reason: ArticleCancellationDetail['reason'] | null;
  otherReason: string | null;
};

export type Choices = Record<string, Choice>;

export const ArticleNameWithChoice = ({
  article,
  showChoices,
  choice,
  setChoice,
  showChoicesError,
}: {
  article: ArticleWithRelations;
  showChoices: boolean;
  choice?: Choice;
  setChoice?: (choice: Choice) => void;
  showChoicesError?: boolean;
}) => {
  const { isMobile } = useViewPort();
  const { view } = useBrandRequestContext();

  const isCompletingValidation = useIsCompletingValidation(article.requestId);

  const showChoiceButton = showChoices && !!choice && !!setChoice;

  return (
    <Stack gap="1rem">
      <Stack row alignItems="center" justifyContent="space-between" gap="0.75rem">
        <ArticleNameCell
          article={article}
          photoSize="medium"
          showWarranty={view.articles.warranty.shown}
          inlineSerialAndRef
          showRetailPrice={
            article.snapshot.serviceChoice === 'exchange' || !!article.exchangedFromId
          }
        />

        {!isMobile && showChoiceButton && (
          <ChoiceButton
            choice={choice}
            setChoice={setChoice}
            isCompletingValidation={isCompletingValidation}
          />
        )}
      </Stack>

      {isMobile && showChoiceButton && (
        <ChoiceButton
          choice={choice}
          setChoice={setChoice}
          isCompletingValidation={isCompletingValidation}
        />
      )}

      {showChoiceButton && choice.value === 'refused' && (
        <ChoiceRefusedInputs
          choice={choice}
          setChoice={setChoice}
          showChoicesError={showChoicesError}
        />
      )}
    </Stack>
  );
};

const ChoiceButton = ({
  choice,
  setChoice,
  isCompletingValidation,
}: {
  choice: Choice;
  setChoice: (choice: Choice) => void;
  isCompletingValidation: boolean;
}) => {
  const { isMobile } = useViewPort();

  if (choice.value === 'refused') {
    return (
      <Button
        variant="secondary"
        size="medium"
        onClick={() =>
          setChoice({
            value: 'accepted',
            reason: null,
            otherReason: null,
          })
        }
        disabled={isCompletingValidation}
        style={isMobile ? { flex: 1 } : undefined}
      >
        <IconUndo />
        <Trans id="client-article-card-header.choice-button.accept">Accept service</Trans>
      </Button>
    );
  }

  return (
    <Button
      variant="secondary-danger"
      size="medium"
      onClick={() =>
        setChoice({
          value: 'refused',
          reason: null,
          otherReason: null,
        })
      }
      disabled={isCompletingValidation}
      style={isMobile ? { flex: 1 } : undefined}
    >
      <IconRefusal />
      <Trans id="client-article-card-header.choice-button.refuse">Refuse services</Trans>
    </Button>
  );
};

const ChoiceRefusedInputs = ({
  choice,
  setChoice,
  showChoicesError,
}: {
  choice: Choice;
  setChoice: (choice: Choice) => void;
  showChoicesError?: boolean;
}) => {
  const { t, i18n } = useLingui();

  const missingReason = showChoicesError && !choice.reason;
  const missingOtherReason = showChoicesError && choice.reason === 'other' && !choice.otherReason;

  const [otherReason, setOtherReason] = useState<string | undefined>(
    choice.otherReason ?? undefined
  );

  const debouncedSetChoice = useMemo(() => {
    return debounce((choice: Choice) => {
      setChoice(choice);
    }, 500);
  }, [setChoice]);

  return (
    <Stack gap="0.5rem">
      <InputSelect
        aria-label={t({
          id: 'client.request.refusal.reason.label',
          message: 'Refusal reason',
        })}
        placeholder={t({
          id: 'client.request.refusal.reason.placeholder',
          message: 'Select a reason...',
        })}
        variant="select"
        value={choice.reason ? { id: choice.reason } : null}
        isSearchable={false}
        onChange={(reason) => setChoice({ ...choice, reason: reason?.id ?? null })}
        options={CLIENT_ARTICLE_CANCELLATION_REASONS.map(({ id }) => ({ id }))}
        getOptionValue={(option) => option.id}
        getOptionLabel={(option) =>
          i18n._(CLIENT_ARTICLE_CANCELLATION_REASONS.find(({ id }) => id === option.id)!.label)
        }
      />
      {choice.reason === 'other' && (
        <>
          <TextArea
            ariaLabel={t({
              id: 'client.request.refusal.other.placeholder',
              message: 'Specify the reason...',
            })}
            placeholder={t({
              id: 'client.request.refusal.other.placeholder',
              message: 'Specify the reason...',
            })}
            value={otherReason}
            onChange={(evt) => {
              setOtherReason(evt.target.value);
              debouncedSetChoice({ ...choice, otherReason: evt.target.value });
            }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            isInvalid={missingOtherReason}
            // Because the TextArea is in a Table, we need to use a native textarea to avoid
            // the focus trap issue when trying to add characters in the middle of a sentence.
            isNativeTextarea
          />
        </>
      )}
      {(missingReason || missingOtherReason) && (
        <Paragraph color="danger">
          {missingReason ? (
            <Trans id="article-choice-cell.error.missing-reason">Please select a reason</Trans>
          ) : missingOtherReason ? (
            <Trans id="article-choice-cell.error.missing-custom">Please specify the reason</Trans>
          ) : null}
        </Paragraph>
      )}
    </Stack>
  );
};
